import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './SocialAuthButtons.css';

/**
 * Los dos botones de login social, compartidos por Login y Register.
 *
 * Vive en un solo lugar a propósito: las dos pantallas lo montan igual y sólo
 * cambian a dónde navegan después (el panel en una, el onboarding en la otra),
 * así que agregar un proveedor acá lo agrega en las dos.
 */

/**
 * Traduce el error de Firebase a algo que se le pueda mostrar a una persona.
 *
 * Es común a los dos proveedores porque los códigos también lo son. Los dos
 * primeros son los que más se ven y ninguno es culpa de quien está entrando:
 *
 * - `popup-closed-by-user` / `cancelled-popup-request`: cerró la ventana o
 *   apretó el otro botón mientras la primera seguía abierta. No es un error,
 *   así que se devuelve `null` y la pantalla no muestra nada.
 * - `account-exists-with-different-credential`: ya tiene cuenta con ese mismo
 *   mail por otro camino (Google, o mail y contraseña). Firebase corta acá y
 *   decir "error al iniciar sesión" deja a alguien golpeando un botón que nunca
 *   le va a funcionar; lo que necesita es saber por dónde entrar.
 */
function mensajeDeError(error, proveedor) {
  switch (error.code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return null;

    case 'auth/account-exists-with-different-credential':
      return `Ya tenés una cuenta con ese email creada por otro medio. Entrá como lo hiciste la primera vez y después podés vincular ${proveedor} desde tu perfil.`;

    case 'auth/unauthorized-domain':
      return 'Error de configuración: el dominio no está autorizado en Firebase. Contactá al administrador.';

    // Salta cuando el proveedor no está habilitado en la consola de Firebase
    // (Authentication → Sign-in method). Es configuración, no código.
    case 'auth/operation-not-allowed':
      return `El ingreso con ${proveedor} no está habilitado todavía. Probá con tu email y contraseña.`;

    case 'auth/popup-blocked':
      return 'El navegador bloqueó la ventana emergente. Permitila e intentá de nuevo.';

    case 'auth/network-request-failed':
      return 'Error de conexión. Revisá tu internet e intentá de nuevo.';

    default:
      return `Error al iniciar sesión con ${proveedor}`;
  }
}

const SocialAuthButtons = ({ onSuccess, onError }) => {
  const { signInWithGoogle, signInWithFacebook } = useAuth();
  // Qué proveedor tiene la ventana abierta. Sirve para deshabilitar los dos
  // botones mientras tanto: abrir el segundo popup cancela el primero y
  // Firebase lo reporta como `cancelled-popup-request`, un error que la persona
  // no provocó a propósito.
  const [enCurso, setEnCurso] = useState(null);

  const entrarCon = async (proveedor, iniciarSesion) => {
    if (enCurso) return;

    setEnCurso(proveedor);
    try {
      const result = await iniciarSesion();
      if (onSuccess) onSuccess(result);
    } catch (error) {
      console.error(`Error con ${proveedor}:`, error);
      const mensaje = mensajeDeError(error, proveedor);
      if (mensaje && onError) onError(mensaje);
    } finally {
      setEnCurso(null);
    }
  };

  return (
    <div className="social-auth-buttons">
      <button
        type="button"
        className="google-btn"
        onClick={() => entrarCon('Google', signInWithGoogle)}
        disabled={enCurso !== null}
      >
        <svg className="google-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        {enCurso === 'Google' ? 'Abriendo...' : 'Continuar con Google'}
      </button>

      <button
        type="button"
        className="facebook-btn"
        onClick={() => entrarCon('Facebook', signInWithFacebook)}
        disabled={enCurso !== null}
      >
        <svg className="facebook-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.313 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" fill="#1877F2"/>
          <path d="M16.671 15.563l.532-3.49h-3.328v-2.25c0-.949.465-1.874 1.956-1.874h1.513V4.996s-1.373-.235-2.686-.235c-2.741 0-4.533 1.662-4.533 4.669v2.643H7.078v3.49h3.047V24a12.09 12.09 0 003.75 0v-8.437h2.796z" fill="#FFFFFF"/>
        </svg>
        {enCurso === 'Facebook' ? 'Abriendo...' : 'Continuar con Facebook'}
      </button>
    </div>
  );
};

export default SocialAuthButtons;
